import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {HttpBackend, HttpClient, HttpEvent, HttpParams} from '@angular/common/http';
import {BimElement} from 'app/shared/model/bim-element';
import {environment} from '../../../environments/environment';
import {TypeElement} from 'app/shared/model/type-element';
import {ElementBasic} from 'app/shared/model/element-basic';
import {BimElementImage} from 'app/shared/model/bim-element-image';
import {catchError} from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class BimElementService {

    private readonly resourceUrl = environment.apiUrl + '/bim-info';

    private httpNoInterceptor: HttpClient;

    constructor(
        private http: HttpClient,
        private httpBackend: HttpBackend // Client Http permettant de bypasser l'intercepteur qui affiche les messages d'erreur
    ) {
        this.httpNoInterceptor = new HttpClient(httpBackend);
    }

    element(id: string): Observable<BimElement> {
        return this.http.get<BimElement>(`${this.resourceUrl}/elements/${id}`);
    }

    elementBasic(id: string): Observable<BimElement> {
        return this.http.get<BimElement>(`${this.resourceUrl}/elements/${id}?basic=true`);
    }

    /**
     * Renvoie une version basique de l'élément, ou null si une erreur s'est produite.
     */
    elementBasicNoError(id: string): Observable<BimElement | null> {
        return this.httpNoInterceptor.get<BimElement>(`${this.resourceUrl}/elements/${id}?basic=true`).pipe(
            catchError(_ => of(null))
        );
    }

    elementParent(id: string, typeElement: TypeElement): Observable<BimElement> {
        const params = new HttpParams().set('typeElement', typeElement);
        return this.http.get<BimElement>(`${this.resourceUrl}/elements/${id}/parent`, {params});
    }

    elementsAdjacents(id: string): Observable<BimElement[]> {
        return this.http.get<BimElement[]>(`${this.resourceUrl}/elements/${id}/adjacents`);
    }

    elementsChildren(id: string, typeElement: TypeElement): Observable<BimElement[]> {
        const params = new HttpParams().set('typeElement', typeElement);
        return this.http.get<BimElement[]>(`${this.resourceUrl}/elements/${id}/children`, {params});
    }

    basicBatimentChildren(elementId: string, typeElement: TypeElement, typeOuvrageId?: string, includeParents = false): Observable<ElementBasic[]> {
        let params = new HttpParams().set('typeElement', typeElement).set('includeParents', String(includeParents));
        if (typeOuvrageId) {
            params = params.set('typeOuvrageId', typeOuvrageId);
        }
        return this.http.get<ElementBasic[]>(`${this.resourceUrl}/elements/${elementId}/basic-children`, {params});
    }

    getBulkUpdateUrl(elementId: string): string {
        return `${this.resourceUrl}/elements/${elementId}/bulk-update`;
    }

    // *******************
    // IMAGES
    // *******************

    uploadImage(elementId: string, formData: FormData, replacementFileId?: string): Observable<HttpEvent<any>> {
        let params = new HttpParams();
        if (replacementFileId) {
            params = params.set('replacementFileId', replacementFileId);
        }
        return this.http.post<any>(`${this.resourceUrl}/elements/${elementId}/images`, formData, {
            params,
            reportProgress: true,
            observe: 'events'
        });
    }

    deleteImage(elementId: string, fileId: string) {
        return this.http.delete<any>(`${this.resourceUrl}/elements/${elementId}/images/${fileId}`);
    }

    getImageData(elementId: string, image: BimElementImage): Observable<string> {
        return this.httpNoInterceptor.get(`${this.resourceUrl}/elements/${elementId}/images/${image.fileId}/data`, {responseType: 'text'}).pipe(
            catchError(_ => of(''))
        );
    }

}
